import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { LocalCacheService } from '../services/local-cache.service';
import { refreshExpiredSession } from './auth.guard';

/**
 * RBAC route guard for ToroOasis Console.
 *
 * Only users holding at least one admin-like role (any role containing
 * "admin", case-insensitive — e.g. Admin, SuperAdmin, SystemAdmin,
 * SystemMasterAdmin) may activate protected routes. Runs after `authGuard`
 * on every protected route, so an unauthenticated user is still routed to
 * `/sign-in` by the time this guard would otherwise fire; the unauthenticated
 * check here is a defensive fallback in case this guard is ever applied
 * without `authGuard`.
 */
export const roleGuard: CanActivateFn = () => {
  const cache = inject(LocalCacheService);
  const router = inject(Router);

  if (!cache.isAuthenticated) {
    // Same refresh as authGuard (one shared call), so the two guards agree on an expired token.
    const refreshed = refreshExpiredSession();
    if (!refreshed) {
      return router.createUrlTree(['/sign-in']);
    }
    return refreshed.pipe(
      map((ok) => {
        if (!ok) return router.createUrlTree(['/sign-in']);
        return cache.hasAllowedRole ? true : router.createUrlTree(['/access-denied']);
      }),
    );
  }

  if (!cache.hasAllowedRole) {
    return router.createUrlTree(['/access-denied']);
  }

  return true;
};
